import { useEffect, useState, useCallback } from "react";
import { systemApi } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Database, Mail, KeyRound, CheckCircle2, XCircle, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { relativeTime } from "@/lib/format";

export default function SystemHealth() {
  const { user } = useAuth();
  const [pc, setPc] = useState(null);
  const [rot, setRot] = useState([]);
  const [busy, setBusy] = useState(null);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      setPc(await systemApi.pineconeStatus());
      if (isAdmin) setRot(await systemApi.keyRotations());
    } catch (e) { console.error("system health load failed", e); }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const testSmtp = async () => {
    setBusy("smtp");
    try {
      const r = await systemApi.smtpTest();
      if (r.sent) toast.success(`SMTP OK · sent to ${r.to}`);
      else if (r.configured) toast.error(`SMTP configured but send failed`);
      else toast.info("SMTP not configured — set DEAD_MAN_SMTP_* in .env");
    } catch { toast.error("SMTP test failed"); }
    finally { setBusy(null); }
  };

  const rotate = async () => {
    if (!window.confirm("Rotate the vault encryption key? All documents and Gmail credentials will be re-encrypted with a new key.")) return;
    setBusy("rotate");
    try {
      const r = await systemApi.rotateVaultKey();
      toast.success(`Key rotated · ${r.docs_rotated}/${r.docs_total} documents re-encrypted`);
      load();
    } catch { toast.error("Rotation failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="system-health">
      <div>
        <div className="uc-chip mb-2"><Database size={11}/> SYSTEM HEALTH</div>
        <p className="text-white/50 text-xs">Verify integrations and rotate vault keys.</p>
      </div>

      {/* Pinecone */}
      <div className="p-3 border border-white/10 bg-black/40" data-testid="pinecone-status-card">
        <div className="flex items-center justify-between mb-1">
          <span className="font-mono-uc text-[11px] uppercase tracking-widest text-white/70 flex items-center gap-1">
            <Database size={11} className="text-violet-400"/> Vector Memory
          </span>
          {pc?.backend === "pinecone" ? (
            <span className="uc-chip"><CheckCircle2 size={10} className="text-emerald-400"/><span className="text-emerald-400">Pinecone</span></span>
          ) : (
            <span className="uc-chip"><XCircle size={10} className="text-white/40"/><span className="text-white/60">Keyword Fallback</span></span>
          )}
        </div>
        {pc?.backend === "pinecone" ? (
          <div className="text-[11px] text-white/60 font-mono-uc">
            index: {pc.index_name} · {pc.cloud}/{pc.region} · vectors: {pc.vector_count ?? "?"}
          </div>
        ) : (
          <div className="text-[10px] text-white/40 leading-relaxed">
            {pc?.hint || "Add PINECONE_API_KEY to /app/backend/.env and restart backend to enable semantic vector search."}
          </div>
        )}
        <button data-testid="pinecone-refresh-btn" onClick={load} className="mt-2 text-[10px] font-mono-uc uppercase tracking-wider text-white/50 hover:text-amber-400 flex items-center gap-1">
          <RefreshCw size={9}/> Recheck
        </button>
      </div>

      {/* SMTP */}
      <div className="p-3 border border-white/10 bg-black/40" data-testid="smtp-card">
        <div className="flex items-center justify-between mb-1">
          <span className="font-mono-uc text-[11px] uppercase tracking-widest text-white/70 flex items-center gap-1">
            <Mail size={11} className="text-blue-400"/> Dead Man's Switch Delivery
          </span>
        </div>
        <div className="text-[10px] text-white/40 mb-2">Sends to your trusted contact when the switch trips. Requires DEAD_MAN_SMTP_* env.</div>
        <Button data-testid="smtp-test-btn" onClick={testSmtp} disabled={busy === "smtp"} variant="ghost" className="rounded-none h-8 text-[11px] border border-white/10 text-white/70 hover:bg-white/5 w-full">
          {busy === "smtp" ? <Loader2 className="animate-spin" size={11}/> : "Send SMTP test to trusted contact"}
        </Button>
      </div>

      {/* Vault Key Rotation - admin only */}
      {isAdmin && (
        <div className="p-3 border border-amber-500/25 bg-amber-500/[0.03]" data-testid="rotate-card">
          <div className="flex items-center justify-between mb-1">
            <span className="font-mono-uc text-[11px] uppercase tracking-widest text-amber-400 flex items-center gap-1">
              <KeyRound size={11}/> Vault Key Rotation
            </span>
          </div>
          <div className="text-[10px] text-white/50 mb-2">Generates a new Fernet key, re-encrypts every document + Gmail credential, archives the old key.</div>
          <Button data-testid="rotate-key-btn" onClick={rotate} disabled={busy === "rotate"} className="uc-btn-amber rounded-none h-9 text-xs w-full">
            {busy === "rotate" ? <Loader2 className="animate-spin mr-1" size={12}/> : <KeyRound size={12} className="mr-1"/>}
            Rotate Vault Key
          </Button>
          {rot.length > 0 && (
            <div className="mt-3 space-y-1" data-testid="rotation-history">
              <div className="font-mono-uc text-[9px] uppercase tracking-widest text-white/40">Recent Rotations</div>
              {rot.slice(0, 4).map((r) => (
                <div key={r.id} className="text-[10px] text-white/60 font-mono-uc flex justify-between">
                  <span>{r.docs_rotated}/{r.docs_total} docs · {r.rotated_by}</span>
                  <span className="text-white/40">{relativeTime(r.rotated_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
